import { useState } from "react";
import { List, Searchbar, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import ItemList from "../ItemList";
import type { ListItem } from "../ItemList";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingBox } from "./components/OnboardingBox";

type Props = {
	title: string;
	description?: string;
};

const SEARCH_THRESHOLD = 5;

const StateAvatar = ({ abbr }: { abbr: string }) => {
	const theme = useAppTheme();
	return (
		<Box
			width={40}
			height={40}
			borderRadius={20}
			justifyContent="center"
			alignItems="center"
			style={{ backgroundColor: theme.colors.primaryContainer }}
		>
			<Text
				variant="titleSmall"
				style={{ color: theme.colors.onPrimaryContainer }}
			>
				{abbr}
			</Text>
		</Box>
	);
};

export const OnboardingStateScreen = ({ title, description }: Props) => {
	const theme = useAppTheme();
	const { derivedStates, selectedStateCode, onSelectState } = useOnboarding();
	const [searchValue, setSearchValue] = useState("");

	const showSearch = derivedStates.length > SEARCH_THRESHOLD;

	const filteredStates = searchValue
		? derivedStates.filter((s) =>
				s.name.toLowerCase().includes(searchValue.toLowerCase()),
			)
		: derivedStates;

	const items: ListItem[] = filteredStates.map((state) => ({
		id: state.code,
		title: state.name,
		customLeft: (p) => (
			<List.Icon {...p} icon={() => <StateAvatar abbr={state.code} />} />
		),
		customRight: () =>
			selectedStateCode === state.code ? (
				<List.Icon icon="check" color={theme.colors.primary} />
			) : null,
	}));

	const emptyState = {
		title: "No states found",
		subtitle: "Try adjusting your search query",
	};

	return (
		<Box flex={1} gap={16}>
			<OnboardingBox>
				<Box gap={14}>
					<Text variant="titleLarge">{title}</Text>
					{description ? <Text variant="bodyMedium">{description}</Text> : null}
				</Box>
				{showSearch ? (
					<Searchbar
						value={searchValue}
						onChangeText={setSearchValue}
						placeholder="Search your state..."
					/>
				) : null}
			</OnboardingBox>
			<Box flex={1}>
				<ItemList
					items={items}
					onSelect={onSelectState}
					selectedId={selectedStateCode}
					emptyState={emptyState}
				/>
			</Box>
		</Box>
	);
};
